// ════════════════════════════════════════════════════════════════════════════
//  COSMIC ANALYTICS KIT — shared building blocks for every admin dashboard.
//  Palette is CVD-validated for the dark surface (#150E2E):
//  gold #B8893C · tulsi #27A87C · violet #8F5EF2 · lotus #D96A88  (fixed order)
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

// SVG ids must not contain spaces/punctuation — slugify so gradient fills always resolve.
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-');

export const CHART = {
  gold: '#B8893C',
  tulsi: '#27A87C',
  violet: '#8F5EF2',
  lotus: '#D96A88',
  categorical: ['#B8893C', '#27A87C', '#8F5EF2', '#D96A88'],
  grid: 'rgba(229,192,107,0.10)',
  axis: '#AEA08A',
};

// Short month label ("Aug") → most recent matching "YYYY-MM" within the last
// 12 months — lets label-only series drill into the month endpoint.
export const monthKeyFromLabel = (label) => {
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    if (d.toLocaleString('en-US', { month: 'short' }) === label) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
  }
  return null;
};

export const fmtINR = (n) =>
  '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
export const fmtNum = (n) => (Number(n) || 0).toLocaleString('en-IN');

/* ---- animated count-up (rAF, eases out, re-runs when target changes) ---- */
export const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target) || 0;
    if (from === to) { setVal(to); return; }
    let raf; const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

/* ---- polling hook: refetch every `ms`, expose seconds-since-update ---- */
export const useLivePoll = (fetcher, ms = 15000) => {
  const [tick, setTick] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [ago, setAgo] = useState(0);
  const run = useCallback(async () => {
    try { await fetcher(); setUpdatedAt(Date.now()); } catch (e) { /* keep last data */ }
  }, [fetcher]);
  useEffect(() => { run(); const id = setInterval(() => { run(); setTick(t => t + 1); }, ms); return () => clearInterval(id); }, [run, ms]);
  useEffect(() => { const id = setInterval(() => updatedAt && setAgo(Math.round((Date.now() - updatedAt) / 1000)), 1000); return () => clearInterval(id); }, [updatedAt]);
  return { ago, tick, refresh: run };
};

/* ---- LIVE pulse + updated-ago ---- */
export const LivePulse = ({ ago }) => (
  <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aura opacity-60"></span>
      <span className="relative inline-flex h-2 w-2 rounded-full bg-aura"></span>
    </span>
    LIVE {ago != null && <span className="text-stone-500 normal-case tracking-normal">· {ago}s ago</span>}
  </span>
);

/* ---- glass KPI tile with count-up + optional sparkline ---- */
export const KpiTile = ({ icon, label, value, money, accent = CHART.gold, spark, suffix }) => {
  const v = useCountUp(value);
  return (
    <div className="glass-card relative overflow-hidden p-5 transition-transform duration-300 hover:-translate-y-1">
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-[0.12] blur-2xl" style={{ background: accent }} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-muted">
            <span className="material-symbols-outlined text-[16px]" style={{ color: accent }}>{icon}</span>
            <span className="truncate">{label}</span>
          </div>
          <div className="mt-2 font-display text-3xl font-bold tabular-nums text-ivory">
            {money ? fmtINR(v) : fmtNum(Math.round(v))}{suffix || ''}
          </div>
        </div>
      </div>
      {spark && spark.length > 1 && (
        <div className="mt-3 h-9 -mx-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`sg-${slug(label)}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={2} fill={`url(#sg-${slug(label)})`} dot={false} isAnimationActive={true} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ---- glass chart card wrapper ---- */
export const ChartCard = ({ title, subtitle, right, children, className = '' }) => (
  <div className={`glass-card p-5 md:p-6 ${className}`}>
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-lg font-bold text-ivory">{title}</h3>
        {subtitle && <p className="mt-0.5 text-[11px] font-semibold text-muted">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

/* ---- dark-glass recharts tooltip ---- */
export const CosmicTooltip = ({ active, payload, label, money = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[rgba(229,192,107,.28)] bg-[rgba(14,9,29,.96)] px-3.5 py-2.5 shadow-2xl backdrop-blur-xl">
      <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-muted">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-[12px] font-bold text-ivory tabular-nums">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-stone-300 font-semibold">{p.name}:</span> {money ? fmtINR(p.value) : fmtNum(p.value)}
        </p>
      ))}
    </div>
  );
};

/* ---- status chip for order feeds ---- */
const STATUS_STYLE = {
  Delivered: 'bg-aura/15 text-aura border-aura/30',
  Shipped: 'bg-violet/15 text-violet border-violet/30',
  Packed: 'bg-gold/15 text-gold border-gold/30',
  Ordered: 'bg-saffron/15 text-saffron border-saffron/30',
  Cancelled: 'bg-red-500/15 text-red-400 border-red-500/30',
};
export const StatusChip = ({ status }) => (
  <span className={`rounded-full border px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${STATUS_STYLE[status] || 'bg-white/10 text-stone-300 border-white/15'}`}>
    {status}
  </span>
);

/* ---- TrendBand: compact real-data flow strip for list panels (Orders/Donations) ----
   Buckets the ALREADY-FETCHED docs into the last 8 weeks — zero extra requests. */
export const TrendBand = ({ docs = [], amountKey = 'amount', title, accent = CHART.gold }) => {
  const now = Date.now(), WEEK = 7 * 24 * 3600 * 1000;
  const buckets = Array.from({ length: 8 }, (_, i) => ({ label: `${8 - i}w`, v: 0, n: 0 }));
  let total = 0;
  for (const d of docs) {
    const t = new Date(d.createdAt).getTime();
    if (Number.isNaN(t)) continue;
    const idx = 7 - Math.floor((now - t) / WEEK);
    const amt = Number(d[amountKey]) || 0;
    total += amt;
    if (idx >= 0 && idx < 8) { buckets[idx].v += amt; buckets[idx].n += 1; }
  }
  const recent = buckets.reduce((s, b) => s + b.v, 0);
  return (
    <div className="glass-card mb-6 flex flex-col gap-4 p-4 md:flex-row md:items-center">
      <div className="shrink-0 md:w-56">
        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-muted">{title}</p>
        <p className="mt-1 font-display text-2xl font-bold tabular-nums text-ivory">{fmtINR(total)}</p>
        <p className="text-[10px] font-bold text-muted">{fmtNum(docs.length)} records · {fmtINR(recent)} in 8 weeks</p>
      </div>
      <div className="h-20 min-w-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={buckets} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <defs>
              <linearGradient id={`tb-${slug(title)}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="v" name="₹" stroke={accent} strokeWidth={2} fill={`url(#tb-${slug(title)})`} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="flex shrink-0 items-center gap-2 text-[9px] font-black uppercase tracking-widest text-muted">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aura opacity-60"></span>
          <span className="relative inline-flex h-2 w-2 rounded-full bg-aura"></span>
        </span>
        Real-time
      </div>
    </div>
  );
};

/* ---- live IST clock — the sanctum runs on centre time ---- */
export const LiveClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const time = now.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  return (
    <span className="glass-card flex items-center gap-2 rounded-xl px-3.5 py-2 text-[11px] font-black uppercase tracking-widest text-gold-light tabular-nums">
      <span className="material-symbols-outlined text-[15px] text-gold">schedule</span>
      {time.toUpperCase()} <span className="text-muted">IST</span>
    </span>
  );
};

/* ---- month-over-month delta badge (real math from the series) ---- */
export const DeltaBadge = ({ series }) => {
  if (!series || series.length < 2) return null;
  const prev = Number(series[series.length - 2]) || 0;
  const curr = Number(series[series.length - 1]) || 0;
  if (!prev && !curr) return null;
  const pct = prev ? ((curr - prev) / prev) * 100 : 100;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-black tabular-nums ${up ? 'bg-aura/15 text-aura' : 'bg-red-500/15 text-red-400'}`}>
      <span className="material-symbols-outlined text-[13px]">{up ? 'trending_up' : 'trending_down'}</span>
      {Math.abs(pct).toFixed(0)}% MoM
    </span>
  );
};
